import { useCallback, useMemo } from "react";
import { useRouter } from "solito/navigation";
import { Mail as MailIcon, MessageCircle as MessageCircleIcon, MoreHorizontal as MoreHorizontalIcon, Phone as PhoneIcon, TabletSmartphone as TabletSmartphoneIcon } from "@tamagui/lucide-icons";
import { MfaMethodType, webAgent__EndpointSupportLevel, webAgent__EndpointType } from "@medbillai/generated-graphql-types";
import type { InsuranceLinkType } from "@medbillai/utils";
import { accountsLinkErrorUrl, accountsLinkSelectUrl, accountsLinkUnsupportedPlanUrl, accountsLinkWebAgentAlternateInsuranceUrl, accountsLinkWebAgentLinkUrl, accountsLinkWebAgentMfaCodeUrl, accountsLinkWebAgentMfaSelectUrl, accountsLinkWebAgentNotificationsUrl, accountsLinkWebAgentSecurityQuestionUrl, accountsLinkWebAgentSuccessUrl, accountsUrl, onboardingCompletedUrl } from "@medbillai/utils/urls";
import { type AccountLinkPresentation, useAccountLinkDataStore } from "./accountLinkStore";
import type { Endpoint } from "./types";

/**
 * Slugs used to identify the Big 4 endpoints so they are recommended first
 */
export const RECOMMENDED_ENDPOINT_SLUGS = ["aetna", "anthem-blue-cross", "cigna", "unitedhealthcare"];
const RecommendedEhrEndpoints = ["cedars-sinai", "nyu-langone", "northwestern-medicine", "ucsf"];

/**
 * Check if an endpoint is an EHR endpoint based on its type
 */
const isEhrEndpoint = (endpoint: FilterableEndpoint): boolean => endpoint.type === webAgent__EndpointType.EHR;

/**
 * Sort endpoints by a recommended list, putting recommended items first in order
 */
const sortByRecommended = <T extends FilterableEndpoint,>(endpoints: T[], recommendedList: string[]): T[] => {
  return endpoints.sort((a, b) => {
    const aIsRecommended = recommendedList.includes(a.slug || "");
    const bIsRecommended = recommendedList.includes(b.slug || "");
    if (aIsRecommended && !bIsRecommended) {
      return -1;
    }
    if (!aIsRecommended && bIsRecommended) {
      return 1;
    }
    return 0;
  });
};
export type FilterableEndpoint = {
  slug?: string | null;
  organization?: {
    id: string;
  } | null;
  type: webAgent__EndpointType;
  currentSupportLevel?: {
    supportLevel: webAgent__EndpointSupportLevel;
  } | null;
};
export const filterEndpoints = <T extends FilterableEndpoint,>(endpoints: T[], linkType: InsuranceLinkType = webAgent__EndpointType.INSURANCE, showEhrDefaultLinks = false) => {
  const fetchedEndpoints = endpoints;

  // Filter for P0 insurance endpoints (non-EHR)
  const getP0Insurers = () => fetchedEndpoints.filter(endpoint => endpoint.currentSupportLevel?.supportLevel === webAgent__EndpointSupportLevel.P0 && !isEhrEndpoint(endpoint));

  // Filter for EHR endpoints (no P0 requirement)
  const getEhrEndpoints = () => fetchedEndpoints.filter(isEhrEndpoint);

  // If feature flag is off, use old logic (insurance only)
  // or if the link type is insurance, only show insurance endpoints
  if (!showEhrDefaultLinks || linkType === webAgent__EndpointType.INSURANCE) {
    const sortedP0Endpoints = sortByRecommended(getP0Insurers(), RECOMMENDED_ENDPOINT_SLUGS);
    return {
      popularEndpoints: sortedP0Endpoints.slice(0, 8),
      fetchedEndpoints
    };
  }
  if (linkType === webAgent__EndpointType.EHR) {
    const ehrEndpoints = getEhrEndpoints();
    const sortedEhrs = sortByRecommended(ehrEndpoints, RecommendedEhrEndpoints);
    return {
      popularEndpoints: sortedEhrs.slice(0, 8),
      fetchedEndpoints: ehrEndpoints
    };
  }

  // Both - interleave 4 insurers + 4 EHRs
  const sortedInsurers = sortByRecommended(getP0Insurers(), RECOMMENDED_ENDPOINT_SLUGS);
  const sortedEhrs = sortByRecommended(getEhrEndpoints(), RecommendedEhrEndpoints);
  const popularEndpoints: T[] = [...sortedInsurers.slice(0, 4), ...sortedEhrs.slice(0, 4)];
  return {
    popularEndpoints,
    fetchedEndpoints
  };
};
export const getLogoSlug = (endpoint?: Endpoint, orgSlug?: string | null) => endpoint?.slug ?? endpoint?.organization?.slug ?? orgSlug?.toLowerCase() ?? "granted";
export const getLogoLetter = (endpoint?: Endpoint, orgSlug?: string | null) => endpoint?.displayName?.charAt(0).toUpperCase() ?? orgSlug?.charAt(0).toUpperCase() ?? "G";
export const getMfaMethodLabel = (method: MfaMethodType) => {
  switch (method) {
    case MfaMethodType.SMS:
      return "Text Message";
    case MfaMethodType.PHONE:
      return "Phone Call";
    case MfaMethodType.EMAIL:
      return "Email";
    case MfaMethodType.AUTHENTICATOR:
      return "Authenticator";
    default:
      return "Unknown";
  }
};
export const getMfaIcon = (method: MfaMethodType) => {
  switch (method) {
    case MfaMethodType.SMS:
      return MessageCircleIcon;
    case MfaMethodType.PHONE:
      return PhoneIcon;
    case MfaMethodType.EMAIL:
      return MailIcon;
    case MfaMethodType.AUTHENTICATOR:
      return TabletSmartphoneIcon;
    default:
      return MoreHorizontalIcon;
  }
};

/**
 * Hook that provides navigation URLs for the account link flow.
 * Reads isOnboarding and exitUrl from the store to determine exit URLs.
 */
export const useAccountLinkNavigation = () => {
  const isOnboarding = useAccountLinkDataStore(state => state.isOnboarding);
  const exitUrl = useAccountLinkDataStore(state => state.exitUrl);
  const computedExitUrl = exitUrl ?? (isOnboarding ? onboardingCompletedUrl() : accountsUrl());
  return useMemo(() => ({
    start: accountsLinkSelectUrl(),
    error: accountsLinkErrorUrl(),
    exit: computedExitUrl,
    select: accountsLinkSelectUrl(),
    unsupportedPlan: accountsLinkUnsupportedPlanUrl(),
    webAgent: {
      start: accountsLinkWebAgentLinkUrl(),
      mfaSelect: accountsLinkWebAgentMfaSelectUrl(),
      mfaCode: accountsLinkWebAgentMfaCodeUrl(),
      securityQuestion: accountsLinkWebAgentSecurityQuestionUrl(),
      success: (hasBenefitsSummary: boolean) => accountsLinkWebAgentSuccessUrl({
        hasBenefitsSummary
      }),
      notifications: accountsLinkWebAgentNotificationsUrl(),
      alternateInsurance: isOnboarding ? onboardingCompletedUrl() : accountsLinkWebAgentAlternateInsuranceUrl()
    }
  }), [isOnboarding, computedExitUrl]);
};
export type AccountLinkNavigationType = ReturnType<typeof useAccountLinkNavigation>;

// Keep old name for backwards compatibility during migration
export const useInsuranceNavigation = useAccountLinkNavigation;
export type InsuranceNavigationType = AccountLinkNavigationType;

/**
 * Hook to start the account link flow.
 * Sets presentation, isOnboarding, and exitUrl in the store before navigating.
 */
export const useStartAccountLink = () => {
  const resetStore = useAccountLinkDataStore(state => state.reset);
  const setPresentation = useAccountLinkDataStore(state => state.setPresentation);
  const setIsOnboarding = useAccountLinkDataStore(state => state.setIsOnboarding);
  const setExitUrl = useAccountLinkDataStore(state => state.setExitUrl);
  const setIssueId = useAccountLinkDataStore(state => state.setIssueId);
  const {
    push
  } = useRouter();
  return useCallback(({
    isOnboarding = false,
    presentation = "modal",
    exitUrl,
    linkType,
    issueId
  }: {
    isOnboarding?: boolean;
    presentation?: AccountLinkPresentation;
    exitUrl?: string;
    linkType?: InsuranceLinkType;
    issueId?: string;
  } = {}) => {
    // Reset store at the start of a new flow to ensure clean state
    resetStore();
    setPresentation(presentation);
    setIsOnboarding(isOnboarding);
    setExitUrl(exitUrl ?? null);
    setIssueId(issueId ?? null);
    push(accountsLinkSelectUrl(linkType ? {
      placeFrom: "insuranceHome",
      linkType
    } : undefined));
  }, [push, resetStore, setPresentation, setIsOnboarding, setExitUrl, setIssueId]);
};
export { useAccountLinkAnalytics } from "./useAccountLinkAnalytics";
// Note: useCloseAccountLink is exported from ./useCloseAccountLink directly
// to avoid Jest ESM issues with solito/router imports